/* oxlint-disable typescript/consistent-type-assertions */
import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	useEffect,
	useRef,
} from 'react';
import { usePreloadedQuery, graphql, type PreloadedQuery } from 'react-relay';
import {
	useRightSidebarController,
	useActiveRightSidebarState,
} from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import type { RecommendationPanelContextQuery } from './__generated__/RecommendationPanelContextQuery.graphql';

// Types based on the GraphQL response
type RecommendationAction = {
	name: string;
	link: string;
};

export type RecommendationResource = {
	name: string;
	link: string;
};

export type RecommendationStatus = 'INFO' | 'WARNING' | 'CRITICAL';

export type RecommendationCategory =
	| 'CFO_CUSTOM_FIELD'
	| 'CFO_WORK_TYPE_LIMIT'
	| 'CFO_PROJECT_CLEANUP'
	| 'CFO_FIELD_LIMIT'
	| 'CFO_BOARD_PERFORMANCE'
	| 'CFO_BROWSER'
	| 'CFO_HARDWARE'
	| 'CFO_NETWORK_PROTOCOL'
	| 'CFO_NETWORK_SPEED'
	| 'CFO_OPERATING_SYSTEM';

type RecommendationGroup = 'LIMITS_AND_CLEANUP' | 'USER_SETUP';

export type RecommendationData = {
	recommendationCategory: RecommendationCategory;
	name: string;
	description: string;
	group: RecommendationGroup;
	actions: RecommendationAction[];
	resources: RecommendationResource[];
	created: string;
	status: RecommendationStatus;
};
export type RecommendationGroupCategory = 'LIMITS_AND_CLEANUP' | 'USER_SETUP';

type RecommendationGroupData = {
	name: string;
	description: string;
	groupCategory: RecommendationGroupCategory;
	sidePanelTitle: string;
	sidePanelDescription: string;
	status: RecommendationStatus;
};

// Map structures
type RecommendationsMap = Map<RecommendationCategory, RecommendationData>;
// Map of group category to array of recommendation categories belonging to that group
type GroupToRecommendationsMap = Map<RecommendationGroupCategory, RecommendationCategory[]>;

// Split context: Static data that never changes after load
type RecommendationDataContextType = {
	recommendationsMap: RecommendationsMap | null;
	recommendationGroupsArray: RecommendationGroupData[] | null;
	groupToRecommendationsMap: GroupToRecommendationsMap | null;
	preloadedQueryRef: PreloadedQuery<RecommendationPanelContextQuery> | null;
};

// Split context: Dynamic UI state
type RecommendationUIContextType = {
	isOpen: boolean;
	selectedCardId: RecommendationGroupCategory | null;
	openPanel: (cardId: RecommendationGroupCategory) => void;
	closePanel: () => void;
	clearSelections: () => void;
};

export const RecommendationDataContext: React.Context<RecommendationDataContextType | undefined> =
	createContext<RecommendationDataContextType | undefined>(undefined);

export const RecommendationUIContext: React.Context<RecommendationUIContextType | undefined> =
	createContext<RecommendationUIContextType | undefined>(undefined);

type RecommendationPanelProviderProps = {
	children: React.ReactNode;
	preloadedQuery: PreloadedQuery<RecommendationPanelContextQuery> | null;
};

// Default provider when experiment is off (no query available)
const DefaultRecommendationPanelProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [isOpen] = useState(false);
	const [selectedCardId] = useState<RecommendationGroupCategory | null>(null);

	// No-op callbacks when experiment is off
	const openPanel = useCallback(() => {
		// No-op: Panel functionality disabled when experiment is off
		return undefined;
	}, []);

	const closePanel = useCallback(() => {
		// No-op: Panel functionality disabled when experiment is off
		return undefined;
	}, []);

	const clearSelections = useCallback(() => {
		// No-op: Panel functionality disabled when experiment is off
		return undefined;
	}, []);

	const dataValue = useMemo(
		() => ({
			recommendationsMap: null,
			recommendationGroupsArray: null,
			groupToRecommendationsMap: null,
			preloadedQueryRef: null,
		}),
		[],
	);

	const uiValue = useMemo(
		() => ({
			isOpen,
			selectedCardId,
			openPanel,
			closePanel,
			clearSelections,
		}),
		[isOpen, selectedCardId, openPanel, closePanel, clearSelections],
	);

	return (
		<RecommendationDataContext.Provider value={dataValue}>
			<RecommendationUIContext.Provider value={uiValue}>
				{children}
			</RecommendationUIContext.Provider>
		</RecommendationDataContext.Provider>
	);
};

export const RecommendationPanelProvider: React.FC<RecommendationPanelProviderProps> = ({
	children,
	preloadedQuery,
}) => {
	// If no query available, use default provider
	if (!preloadedQuery) {
		return <DefaultRecommendationPanelProvider>{children}</DefaultRecommendationPanelProvider>;
	}

	return (
		<RecommendationPanelProviderWithQuery preloadedQuery={preloadedQuery}>
			{children}
		</RecommendationPanelProviderWithQuery>
	);
};

export const RECOMMENDATION_PANEL_ID = 'performance-recommendations-panel';

// Minimal bridge for cross-boundary communication
// SafeRecommendationPanel is rendered outside the provider tree (in right sidebar layout)
// These refs allow it to access the necessary data without complex global state
type ProviderBridge = {
	uiContext: RecommendationUIContextType | null;
	dataContext: RecommendationDataContextType | null;
	listeners: Set<() => void>;
};

const providerBridge: ProviderBridge = {
	uiContext: null,
	dataContext: null,
	listeners: new Set(),
};

export const getProviderBridge = (): ProviderBridge => providerBridge;

export const subscribeToBridgeChanges = (listener: () => void): (() => void) => {
	providerBridge.listeners.add(listener);
	return () => {
		providerBridge.listeners.delete(listener);
	};
};

const notifyBridgeListeners = () => {
	providerBridge.listeners.forEach((listener) => listener());
};

// Provider with actual query data
const RecommendationPanelProviderWithQuery: React.FC<{
	children: React.ReactNode;
	preloadedQuery: PreloadedQuery<RecommendationPanelContextQuery>;
}> = ({ children, preloadedQuery }) => {
	const { openRightSidebar, closeRightSidebar } = useRightSidebarController();
	const rightSidebarState = useActiveRightSidebarState();

	// UI State
	const [isOpen, setIsOpen] = useState(false);
	const [selectedCardId, setSelectedCardId] = useState<RecommendationGroupCategory | null>(null);

	// Store refs for cross-boundary access
	const uiContextValueRef = useRef<RecommendationUIContextType | null>(null);
	const dataContextValueRef = useRef<RecommendationDataContextType | null>(null);

	// Load preloaded query
	const data = usePreloadedQuery(
		graphql`
			query RecommendationPanelContextQuery($cloudId: ID!) {
				jira @required(action: THROW) {
					cfoRecommendations(cloudId: $cloudId)
						@optIn(to: "JiraCFORecommendationConnection")
						@required(action: THROW) {
						edges @required(action: THROW) {
							node @required(action: THROW) {
								recommendationCategory @required(action: THROW)
								name @required(action: THROW)
								description @required(action: THROW)
								group @required(action: THROW)
								actions @required(action: THROW) {
									name @required(action: THROW)
									link @required(action: THROW)
								}
								resources @required(action: THROW) {
									name @required(action: THROW)
									link @required(action: THROW)
								}
								created @required(action: THROW)
								status @required(action: THROW)
							}
						}
					}
					cfoRecommendationGroups(cloudId: $cloudId)
						@optIn(to: "JiraCFORecommendationGroupDetailConnection")
						@required(action: THROW) {
						edges @required(action: THROW) {
							node @required(action: THROW) {
								name @required(action: THROW)
								description @required(action: THROW)
								groupCategory @required(action: THROW)
								sidePanelTitle @required(action: THROW)
								sidePanelDescription @required(action: THROW)
								status @required(action: THROW)
							}
						}
					}
				}
			}
		`,
		preloadedQuery,
	);

	// Process preloaded data synchronously - Relay handles errors via @required(action: THROW)
	// Using useMemo instead of useEffect ensures data is available on first render, preventing layout shift
	// This data is static and never changes, perfect for the data context
	const { recommendationsMap, recommendationGroupsArray, groupToRecommendationsMap } =
		useMemo(() => {
			const jira = data.jira;

			// Map recommendations by category
			const recMap = new Map<RecommendationCategory, RecommendationData>();
			for (const edge of jira.cfoRecommendations.edges) {
				if (edge) {
					const node = edge.node;
					const actions = node.actions.filter(Boolean).map((action) => ({
						name: action.name || '',
						link: action.link || '',
					}));
					const resources = node.resources.filter(Boolean).map((resource) => ({
						name: resource.name || '',
						link: resource.link || '',
					}));

					recMap.set(node.recommendationCategory as RecommendationCategory, {
						recommendationCategory: node.recommendationCategory as RecommendationCategory,
						name: node.name,
						description: node.description,
						group: node.group as RecommendationGroup,
						actions,
						resources,
						created: node.created,
						status: node.status as RecommendationStatus,
					});
				}
			}

			// Map recommendation groups by category
			const groupMap: RecommendationGroupData[] = [];
			for (const edge of jira.cfoRecommendationGroups.edges) {
				if (edge) {
					const node = edge.node;

					groupMap.push({
						name: node.name,
						description: node.description,
						groupCategory: node.groupCategory as RecommendationGroupCategory,
						sidePanelTitle: node.sidePanelTitle,
						sidePanelDescription: node.sidePanelDescription,
						status: node.status as RecommendationStatus,
					});
				}
			}

			// Create map of group category to recommendation categories
			// Format: { "LIMITS_AND_CLEANUP": ["CFO_CUSTOM_FIELD", "CFO_BOARD_PERFORMANCE", ...], "USER_SETUP": [...] }
			const groupToRecsMap = new Map<RecommendationGroupCategory, RecommendationCategory[]>();
			for (const [category, recommendation] of recMap.entries()) {
				const groupCategory = recommendation.group as RecommendationGroupCategory;
				if (!groupToRecsMap.has(groupCategory)) {
					groupToRecsMap.set(groupCategory, []);
				}
				const group = groupToRecsMap.get(groupCategory);
				if (group) {
					group.push(category);
				}
			}

			return {
				recommendationsMap: recMap,
				recommendationGroupsArray: groupMap,
				groupToRecommendationsMap: groupToRecsMap,
			};
		}, [data]);

	// Data context value - static, never changes
	const dataValue = useMemo(
		() => ({
			recommendationsMap,
			recommendationGroupsArray,
			groupToRecommendationsMap,
			preloadedQueryRef: preloadedQuery,
		}),
		[recommendationsMap, recommendationGroupsArray, groupToRecommendationsMap, preloadedQuery],
	);

	// UI Actions - only these cause re-renders
	const openPanel = useCallback(
		(cardId: RecommendationGroupCategory) => {
			setSelectedCardId(cardId);
			setIsOpen(true);

			// Close any other panel that might be open before opening ours
			// This ensures a clean slate - when our panel closes, sidebar will be empty
			const currentPanelId = rightSidebarState?.panelId;
			if (currentPanelId && currentPanelId !== RECOMMENDATION_PANEL_ID) {
				closeRightSidebar(currentPanelId);
			}

			// Open our panel (not using force since we explicitly closed others)
			// Parameters: (panelId, width, persistent?)
			openRightSidebar(RECOMMENDATION_PANEL_ID, 400, false);
		},
		[openRightSidebar, closeRightSidebar, rightSidebarState],
	);

	const closePanel = useCallback(() => {
		// Just close the sidebar - the useEffect listener will automatically clear state
		closeRightSidebar(RECOMMENDATION_PANEL_ID);
	}, [closeRightSidebar]);

	const clearSelections = useCallback(() => {
		setSelectedCardId(null);
	}, []);

	// UI context value - only UI state, changes frequently
	const uiValue = useMemo(
		() => ({
			isOpen,
			selectedCardId,
			openPanel,
			closePanel,
			clearSelections,
		}),
		[isOpen, selectedCardId, openPanel, closePanel, clearSelections],
	);

	// Listen for external panel closes (e.g., when Rovo opens and replaces our panel)
	// Clear selection when our panel is no longer active
	useEffect(() => {
		const currentPanelId = rightSidebarState?.panelId;

		// If we had a selection and now a different panel is active, clear our state
		if (selectedCardId && currentPanelId !== RECOMMENDATION_PANEL_ID) {
			setIsOpen(false);
			setSelectedCardId(null);
		}
	}, [rightSidebarState?.panelId, selectedCardId]);

	// Update bridge for cross-boundary access (SafeRecommendationPanel)
	useEffect(() => {
		uiContextValueRef.current = uiValue;
		providerBridge.uiContext = uiValue;
		notifyBridgeListeners();
	}, [uiValue]);

	useEffect(() => {
		dataContextValueRef.current = dataValue;
		providerBridge.dataContext = dataValue;
		notifyBridgeListeners();
	}, [dataValue]);

	return (
		<RecommendationDataContext.Provider value={dataValue}>
			<RecommendationUIContext.Provider value={uiValue}>
				{children}
			</RecommendationUIContext.Provider>
		</RecommendationDataContext.Provider>
	);
};

// Hook to access static data context
export const useRecommendationData = (): RecommendationDataContextType => {
	const context = useContext(RecommendationDataContext);
	if (!context) {
		throw new Error('useRecommendationData must be used within RecommendationPanelProvider');
	}
	return context;
};

// Hook to access dynamic UI context
export const useRecommendationUI = (): RecommendationUIContextType => {
	const context = useContext(RecommendationUIContext);
	if (!context) {
		throw new Error('useRecommendationUI must be used within RecommendationPanelProvider');
	}
	return context;
};

// Legacy hook for backward compatibility - combines both contexts
// TODO: Migrate consumers to use useRecommendationData and useRecommendationUI separately
export const useRecommendationPanel = (): {
	isOpen: boolean;
	selectedCardId: RecommendationGroupCategory | null;
	openPanel: (cardId: RecommendationGroupCategory) => void;
	closePanel: () => void;
	clearSelections: () => void;
	recommendationsMap: RecommendationsMap | null;
	recommendationGroupsArray: RecommendationGroupData[] | null;
	groupToRecommendationsMap: GroupToRecommendationsMap | null;
	preloadedQueryRef: PreloadedQuery<RecommendationPanelContextQuery> | null;
} => {
	const dataContext = useRecommendationData();
	const uiContext = useRecommendationUI();

	return {
		...dataContext,
		...uiContext,
	};
};

// Legacy context export for backward compatibility
// NOTE: This is deprecated. Use RecommendationDataContext and RecommendationUIContext instead
export const RecommendationPanelContext: React.Context<RecommendationUIContextType | undefined> =
	RecommendationUIContext;
